import { IPlotCoords } from 'interfaces/i-pitch-performance';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { FT_TO_INCHES } from 'lib_ts/classes/math.utilities';
import { DEFAULT_PLATE, IPlateLoc } from 'lib_ts/interfaces/pitches';
import { IAziAltCoordinate } from 'lib_ts/interfaces/pitches/i-base';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { first, mean } from 'lodash';

export const PLATE_PLOT_MAX_IN = 30;

export const plateToPlotCoords = (plate: IPlateLoc): IPlotCoords => ({
  x: (plate.plate_x - DEFAULT_PLATE.plate_x) * FT_TO_INCHES,
  y: (plate.plate_z - DEFAULT_PLATE.plate_z) * FT_TO_INCHES,
});

export const plateErrorToPlotCoords = (error: IPlateLoc): IPlotCoords => ({
  x: error.plate_x * FT_TO_INCHES,
  y: error.plate_z * FT_TO_INCHES,
});

export const isWithinPlatePlot = ({ x, y }: IPlotCoords) =>
  Math.abs(x) <= PLATE_PLOT_MAX_IN && Math.abs(y) <= PLATE_PLOT_MAX_IN;

export const getShotPlateError = (shot: IMachineShot) => {
  if (!shot.traj || !shot.target_traj) {
    return undefined;
  }

  const actual = TrajHelper.getPlateLoc(shot.traj);
  const target = shot.plate ?? TrajHelper.getPlateLoc(shot.target_traj);

  const output: IPlateLoc = {
    plate_x: actual.plate_x - target.plate_x,
    plate_z: actual.plate_z - target.plate_z,
  };

  return output;
};

export const getAveragePlateError = (shots: IMachineShot[]) => {
  const errors = shots
    .map(getShotPlateError)
    .filter((error): error is IPlateLoc => error !== undefined);

  if (errors.length === 0) {
    return undefined;
  }

  const output: IPlateLoc = {
    plate_x: mean(errors.map((error) => error.plate_x)),
    plate_z: mean(errors.map((error) => error.plate_z)),
  };

  return output;
};

/**
 * Rotates a group's trajectories until their mean reaches the default plate,
 * then removes the small residual introduced by the angular transform.
 */
export const getMeanCenteredPlateLocations = (shots: IMachineShot[]) => {
  const located = shots.flatMap((shot) =>
    shot.traj
      ? [{ shotId: shot._id, plate: TrajHelper.getPlateLoc(shot.traj) }]
      : []
  );

  if (located.length === 0) {
    return [];
  }

  const refPy = first(shots)?.traj?.py;

  if (!refPy) {
    return [];
  }

  const average: IPlateLoc = {
    plate_x: mean(located.map(({ plate }) => plate.plate_x)),
    plate_z: mean(located.map(({ plate }) => plate.plate_z)),
  };

  const averageDelta: IAziAltCoordinate = {
    altitude_rad: -Math.atan((average.plate_z - DEFAULT_PLATE.plate_z) / refPy),
    azimuth_rad: -Math.atan((average.plate_x - DEFAULT_PLATE.plate_x) / refPy),
  };

  const rotated = shots.flatMap((shot) => {
    if (!shot.traj) {
      return [];
    }

    return [
      {
        shotId: shot._id,
        plate: TrajHelper.getPlateLoc(
          TrajHelper.rotateTraj(shot.traj, averageDelta)
        ),
      },
    ];
  });

  const rotatedAverage: IPlateLoc = {
    plate_x: mean(rotated.map(({ plate }) => plate.plate_x)),
    plate_z: mean(rotated.map(({ plate }) => plate.plate_z)),
  };

  const residual: IPlateLoc = {
    plate_x: rotatedAverage.plate_x - DEFAULT_PLATE.plate_x,
    plate_z: rotatedAverage.plate_z - DEFAULT_PLATE.plate_z,
  };

  return rotated.map(({ shotId, plate }) => ({
    shotId,
    plate_x: plate.plate_x - residual.plate_x,
    plate_z: plate.plate_z - residual.plate_z,
  }));
};
